import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LayersIcon from "@mui/icons-material/Layers";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import { ChevronLeft, ChevronRight, Menu, Trash2, X } from "lucide-react";
import { useAuth } from "../../authentication/AuthContext";
import CreateFolderModal from "../../folder/components/CreateFolderModal";
import { useCreateFolder, useDeleteFolder, useFolders } from "../../folder/hooks/useFolder";
import { getNextFolderSortOrder, sortFolders } from "../../folder/utils/folders";

const RECALL_SCOPE = "RECALL";

export default function RecallSidebar() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeFolder = searchParams.get("folder");
    const { authToken } = useAuth();
    const { data: folders = [], error: foldersError } = useFolders(authToken, RECALL_SCOPE);
    const { mutate: createFolder, isLoading: isCreatingFolder, error: createFolderError } = useCreateFolder(authToken, RECALL_SCOPE);
    const { mutate: deleteFolder, isLoading: isDeletingFolder, error: deleteFolderError } = useDeleteFolder(authToken, RECALL_SCOPE);
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [isMobileOpen, setIsMobileOpen] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
    const [showCreateModal, setShowCreateModal] = React.useState(false);

    React.useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setIsCollapsed(true);
                setIsMobileOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const desktopWidthClass = isCollapsed ? "w-[76px]" : "w-[228px]";
    const showDrawer = isMobile ? isMobileOpen : true;
    const sortedFolders = React.useMemo(() => sortFolders(folders), [folders]);
    const activeFolderData = React.useMemo(
        () => sortedFolders.find((folder) => folder.id === activeFolder) ?? null,
        [activeFolder, sortedFolders]
    );
    const canCreateInsideActiveFolder = Boolean(
        activeFolderData && (activeFolderData.parentFolderId ?? null) === null
    );

    const handleCreateFolder = ({ name, parentFolderId }) => {
        const validParentFolderId = sortedFolders.some(
            (folder) => folder.id === parentFolderId && (folder.parentFolderId ?? null) === null
        )
            ? parentFolderId
            : null;

        createFolder(
            {
                name,
                parentFolderId: validParentFolderId,
                sortOrder: getNextFolderSortOrder(sortedFolders, validParentFolderId),
            },
            {
                onSuccess: (createdFolder) => {
                    setShowCreateModal(false);
                    navigate(`/recall?folder=${createdFolder.id}`);
                    if (isMobile) setIsMobileOpen(false);
                },
            }
        );
    };

    const handleDeleteFolder = React.useCallback((folderId) => {
        deleteFolder(folderId, {
            onSuccess: () => {
                if (activeFolder === folderId) {
                    navigate("/recall");
                    if (isMobile) setIsMobileOpen(false);
                }
            },
        });
    }, [activeFolder, deleteFolder, isMobile, navigate]);

    const renderFolderNodes = React.useCallback((parentFolderId = null, depth = 0) => {
        return sortedFolders
            .filter((folder) => (folder.parentFolderId ?? null) === parentFolderId)
            .map((folder) => {
                const isActive = activeFolder === folder.id;

                return (
                    <React.Fragment key={folder.id}>
                        <div
                            className="flex items-center gap-1"
                            style={isCollapsed ? undefined : { paddingLeft: `${12 + depth * 16}px` }}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    navigate(`/recall?folder=${folder.id}`);
                                    if (isMobile) setIsMobileOpen(false);
                                }}
                                className={`flex items-center rounded-lg py-2 text-sm transition-all ${isCollapsed ? "justify-center w-full px-3" : "gap-2.5 flex-1 px-2"} ${
                                    isActive
                                        ? "bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]"
                                        : "text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
                                }`}
                            >
                                {isActive
                                    ? <FolderOpenIcon sx={{ fontSize: 16 }} />
                                    : <FolderIcon sx={{ fontSize: 16 }} />
                                }
                                {!isCollapsed && (
                                    <span className="truncate">
                                        {folder.name}
                                    </span>
                                )}
                            </button>

                            {!isCollapsed && (
                                <button
                                    type="button"
                                    onClick={() => handleDeleteFolder(folder.id)}
                                    disabled={isDeletingFolder}
                                    className="rounded-md p-1.5 text-[var(--text-muted)] transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                                    aria-label={`Delete ${folder.name} folder`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                        {renderFolderNodes(folder.id, depth + 1)}
                    </React.Fragment>
                );
            });
    }, [activeFolder, handleDeleteFolder, isCollapsed, isDeletingFolder, isMobile, navigate, sortedFolders]);

    return (
        <>
            {isMobile && !isMobileOpen && (
                <button
                    type="button"
                    onClick={() => setIsMobileOpen(true)}
                    className="fixed left-4 top-[76px] z-40 rounded-xl border border-[var(--border-color)] bg-[var(--header-bg)] p-2 text-[var(--text-primary)] shadow-lg"
                    aria-label="Open recall sidebar"
                >
                    <Menu size={20} />
                </button>
            )}

            {isMobile && isMobileOpen && (
                <button
                    type="button"
                    onClick={() => setIsMobileOpen(false)}
                    className="fixed inset-0 z-30 bg-black/50"
                    aria-label="Close recall sidebar overlay"
                />
            )}

            <aside
                className={[
                    "z-40 shrink-0 transition-all duration-300 ease-out",
                    isMobile ? "fixed left-0 top-[64px] h-[calc(100vh-64px)]" : "relative min-h-[calc(100vh-64px)]",
                    desktopWidthClass,
                    showDrawer ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0",
                ].join(" ")}
            >
                <div className="m-0 h-full min-h-[calc(100vh-64px)] rounded-2xl border border-[var(--border-color)] bg-[var(--surface)] p-3 shadow-[var(--menu-shadow)] md:ml-1">
                    <div className="mb-2 flex items-center justify-between border-b border-[var(--border-color)] pb-3">
                        {!isCollapsed && (
                            <span className="pl-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">
                                Recall
                            </span>
                        )}

                        <button
                            type="button"
                            onClick={() => {
                                if (isMobile) {
                                    setIsMobileOpen(false);
                                    return;
                                }
                                setIsCollapsed((prev) => !prev);
                            }}
                            className="rounded-lg border border-[var(--border-strong)] p-1.5 text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-hover)]"
                            aria-label={isMobile ? "Close sidebar" : (isCollapsed ? "Expand sidebar" : "Collapse sidebar")}
                        >
                            {isMobile ? <X size={16} /> : (isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />)}
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            navigate("/recall");
                            if (isMobile) setIsMobileOpen(false);
                        }}
                        className={`flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium transition-all ${isCollapsed ? "justify-center" : "gap-2.5"} ${
                            activeFolder
                                ? "text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
                                : "bg-[var(--button-primary-bg)] text-[var(--button-primary-text)]"
                        }`}
                    >
                        <LayersIcon sx={{ fontSize: 17 }} />
                        {!isCollapsed && "All Cards"}
                    </button>

                    <div className="my-3 border-t border-[var(--border-color)]" />

                    {!isCollapsed && (
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)] px-3 mb-2">
                            Folders
                        </p>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowCreateModal(true)}
                        className={`mb-2 flex w-full items-center rounded-lg border border-[var(--border-strong)] px-3 py-2 text-sm font-medium text-[var(--text-secondary)] transition-all hover:border-[var(--button-primary-bg)] hover:bg-[var(--surface-hover)] ${isCollapsed ? "justify-center" : "gap-2.5"}`}
                    >
                        <AddIcon sx={{ fontSize: 16 }} />
                        {!isCollapsed && "New Folder"}
                    </button>

                    {renderFolderNodes()}

                    {!isCollapsed && !sortedFolders.length && !foldersError && (
                        <p className="px-3 pt-1 text-xs text-[var(--text-muted)]">No folders yet.</p>
                    )}

                    {!isCollapsed && foldersError && (
                        <p className="px-3 pt-1 text-xs text-amber-400">Folders could not be loaded yet.</p>
                    )}

                    {!isCollapsed && deleteFolderError && (
                        <p className="px-3 pt-1 text-xs text-amber-400">Folder could not be deleted yet.</p>
                    )}
                </div>
            </aside>

            {showCreateModal && (
                <CreateFolderModal
                    activeFolderId={activeFolder}
                    activeFolderName={activeFolderData?.name}
                    canCreateInsideActiveFolder={canCreateInsideActiveFolder}
                    errorMessage={createFolderError?.message ?? null}
                    isSubmitting={isCreatingFolder}
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateFolder}
                    scopeLabel="Recall"
                />
            )}
        </>
    );
}
